// src/components/proposal-formats/FormatProposalContactSection.tsx

import { useCallback, useEffect, useState } from "react";
import type { OpportunityFormatKey } from "@/types/opportunity";
import {
  getFormatProposalContact,
  upsertFormatProposalContact,
} from "@/lib/opportunity/formatProposalContactClient";
import { ContactBlockFields } from "@/components/document-builder/blocks/ContactBlockFields";
import {
  documentContactFieldsFromPartial,
  emptyDocumentContactFields,
  type DocumentContactFields,
} from "@/lib/document-builder/blocks/contactTypes";
import { ProposalFormatCollapsibleSection } from "./ProposalFormatCollapsibleSection";

export type FormatProposalContactSectionProps = {
  formatKey: OpportunityFormatKey;
  productType: string;
  orgOrgName: string;
  reloadTick: number;
  onSaved: () => void;
};

type Draft = DocumentContactFields & {
  allow_rep_contact_override: boolean;
};

const emptyDraft = (): Draft => ({
  ...emptyDocumentContactFields(),
  allow_rep_contact_override: false,
});

export function FormatProposalContactSection(props: FormatProposalContactSectionProps) {
  const { formatKey, productType, orgOrgName, reloadTick, onSaved } = props;
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [stored, setStored] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await getFormatProposalContact({ format_key: formatKey, product_type: productType });
    setLoading(false);
    if (!r.ok) return;
    const c = r.data.contact;
    const next: Draft = {
      ...documentContactFieldsFromPartial(c),
      allow_rep_contact_override: c?.allow_rep_contact_override ?? false,
    };
    setDraft(next);
    setStored(next);
  }, [formatKey, productType]);

  useEffect(() => {
    void load();
  }, [load, reloadTick]);

  const dirty =
    stored == null ||
    draft.org_name !== stored.org_name ||
    draft.person_name !== stored.person_name ||
    draft.address !== stored.address ||
    draft.phone !== stored.phone ||
    draft.email !== stored.email ||
    draft.allow_rep_contact_override !== stored.allow_rep_contact_override;

  async function handleSave() {
    setSaving(true);
    setMsg(null);
    const r = await upsertFormatProposalContact({
      format_key: formatKey,
      product_type: productType,
      org_name: draft.org_name.trim() || null,
      person_name: draft.person_name.trim() || null,
      address: draft.address.trim() || null,
      phone: draft.phone.trim() || null,
      email: draft.email.trim() || null,
      allow_rep_contact_override: draft.allow_rep_contact_override,
    });
    setSaving(false);
    if (r.ok) {
      const next = { ...draft };
      setStored(next);
      setMsg({ ok: true, text: "Proposal contact saved." });
      onSaved();
    } else {
      setMsg({ ok: false, text: r.error });
    }
  }

  return (
    <ProposalFormatCollapsibleSection
      title="Proposal contact"
      description={`Override org defaults for this product type. Leave fields blank to inherit org settings (org name: ${orgOrgName || "—"}).`}
    >
      {loading ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : (
        <ContactBlockFields
          value={draft}
          onChange={(contact) => setDraft((d) => ({ ...d, ...contact }))}
          orgNamePlaceholder={orgOrgName}
        >
          <label className="flex items-start gap-2 text-xs text-gray-700 dark:text-zinc-300">
            <input
              type="checkbox"
              checked={draft.allow_rep_contact_override}
              onChange={(e) =>
                setDraft((d) => ({ ...d, allow_rep_contact_override: e.target.checked }))
              }
              className="mt-0.5"
            />
            <span>
              Allow sales users to replace contact details with their name, location address,
              location phone, and email on this proposal.
            </span>
          </label>
        </ContactBlockFields>
      )}

      <div className="flex items-center gap-2 justify-end">
        {msg ? (
          <span className={msg.ok ? "text-emerald-600 text-xs" : "text-red-600 text-xs"}>
            {msg.text}
          </span>
        ) : null}
        <button
          type="button"
          disabled={saving || loading || !dirty}
          onClick={() => void handleSave()}
          className="px-2.5 py-1 rounded-lg bg-indigo-600 text-white text-xs disabled:opacity-50"
        >
          Save
        </button>
      </div>
    </ProposalFormatCollapsibleSection>
  );
}
